import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDimBackground } from "../background/useBackgroundDim.js";
import type { StageName } from "@ptp/contracts";
import { fetchPublicConfig } from "../lib/api.js";
import { IdleSparksCanvas } from "../components/IdleSparksCanvas.js";
import { PatternSparksCanvas } from "../components/PatternSparksCanvas.js";
import { ScreenCopy } from "../components/ScreenCopy.js";
// The presentation's own headline file, so both show the same slogan.
import sloganUrl from "../../../visuals/src/paths/slogan_spark.svg?url";
import styles from "./Release.module.css";

/** How often to check whether the projection has finished its pattern. */
const POLL_MS = 8_000;
/** How long "on its way" shows after sending, while the spark flies in on the projection. */
const ON_ITS_WAY_MS = 8_000;

/** End screen: waits among the other sparks, then thanks the visitor once the pattern is complete. */
export function Done({ onRoundReset }: { onRoundReset: () => void }) {
  // Already dark when coming from the launch screen; dark straight away on a direct load.
  useDimBackground();
  const navigate = useNavigate();
  const location = useLocation();
  const [onItsWay, setOnItsWay] = useState(
    () => (location.state as { justSent?: boolean } | null)?.justSent === true,
  );
  // Arrived after the pattern started: no spark of theirs is on the projection.
  const [roundClosed] = useState(
    () => (location.state as { roundClosed?: boolean } | null)?.roundClosed === true,
  );
  const [stage, setStage] = useState<StageName | null>(null);
  const finished = stage === "completed" || (roundClosed && stage !== "collecting");

  useEffect(() => {
    if (!onItsWay) return;
    // Cleared up front, so a reload skips straight to the next screen.
    navigate("/done", { replace: true });
    const timer = setTimeout(() => setOnItsWay(false), ON_ITS_WAY_MS);
    return () => clearTimeout(timer);
  }, [onItsWay, navigate]);

  useEffect(() => {
    // The final screen holds; only a reload checks again.
    if (finished) return;
    let cancelled = false;
    let firstRound: number | null = null;
    const check = async (): Promise<void> => {
      try {
        const config = await fetchPublicConfig();
        if (cancelled) return;
        firstRound ??= config.round;
        // An admin reset started a new round: back to the launch.
        if (config.round !== firstRound) {
          navigate("/", { replace: true });
          onRoundReset();
          return;
        }
        setStage(config.stage.name);
      } catch (err) {
        // The next poll retries; the current screen stays meanwhile.
        console.warn("[join] stage poll failed", err);
      }
    };
    void check();
    const timer = setInterval(() => void check(), POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [finished, navigate, onRoundReset]);

  if (onItsWay) {
    return (
      <div className={`${styles.screen} ${styles.centered}`}>
        <ScreenCopy headline="Your spark is on its way." subline="Look at the projection." />
      </div>
    );
  }

  if (finished) {
    return (
      <div className={`${styles.screen} ${styles.final}`}>
        <div className={styles.patternGlow}>
          <PatternSparksCanvas />
        </div>
        <h1 className={styles.claim}>
          <img src={sloganUrl} alt="Be Part of the Spark" />
        </h1>
        <p>Thank you for being part of this moment.</p>
      </div>
    );
  }

  return (
    <>
      <IdleSparksCanvas />
      <div className={`${styles.screen} ${styles.overCanvas}`}>
        <ScreenCopy
          headline="Your spark is there."
          subline="One of many sparks. The picture is still forming."
        />
      </div>
    </>
  );
}
